import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../lib/api';
import { getIdToken } from '../lib/auth';
import type { MessageStatus, MessageSummary, WaSessionInfo } from '../types';

type ListResponse = { items: MessageSummary[]; nextCursor: string | null };

const STATUS_KEY: Record<MessageStatus, string> = {
  pending: 'status_label.pending',
  sent: 'status_label.sent',
  delivered: 'status_label.delivered',
  read: 'status_label.read',
  failed: 'status_label.failed',
};

export function MessageThread({
  clientId,
  clientName,
  clientPhone,
  firmId,
  caseId,
  channel = 'wa',
}: {
  clientId: string;
  /** Customer's display name. Rendered above each inbound bubble so
   *  the lawyer sees who replied without having to remember the
   *  number. */
  clientName?: string | null;
  clientPhone: string | null;
  firmId: string;
  // When set, filter messages to this case. The send form still targets
  // the clientId (cases don't have phones; sending goes to the client).
  caseId?: string;
  // Channel filter; default 'wa' preserves existing client-page behavior.
  // Pass undefined to load all channels (used by the case Messages tab).
  channel?: 'wa' | 'sms' | 'email' | undefined;
}) {
  const { t, i18n } = useTranslation(['wa', 'common']);
  const [messages, setMessages] = useState<MessageSummary[]>([]);
  const [waStatus, setWaStatus] = useState<WaSessionInfo['status']>('disconnected');
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const streamAbortRef = useRef<AbortController | null>(null);

  async function load() {
    try {
      const params = new URLSearchParams();
      if (caseId) params.set('caseId', caseId);
      else params.set('clientId', clientId);
      if (channel) params.set('channel', channel);
      const data = await apiFetch<ListResponse>(`/api/messages?${params.toString()}`);
      // API returns desc; flip to chronological for display
      setMessages([...data.items].reverse());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  async function loadStatus() {
    try {
      const s = await apiFetch<WaSessionInfo>(`/api/firms/${firmId}/wa/status`);
      setWaStatus(s.status);
    } catch {
      setWaStatus('disconnected');
    }
  }

  useEffect(() => {
    void load();
    void loadStatus();

    // Live updates via SSE. We use fetch + ReadableStream (not
    // EventSource) so we can authenticate via Authorization header.
    // Server pushes every persisted Message for the firm; we filter
    // here by clientId / caseId / channel to match what's rendered.
    const ctrl = new AbortController();
    streamAbortRef.current = ctrl;

    (async () => {
      try {
        const idToken = (await getIdToken()) ?? '';
        const res = await fetch('/api/messages/stream', {
          headers: { Authorization: `Bearer ${idToken}` },
          signal: ctrl.signal,
        });
        if (!res.ok || !res.body) return;
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';
        while (true) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const lines = buf.split('\n');
          buf = lines.pop() ?? '';
          for (const line of lines) {
            const m = /^data: (.+)$/.exec(line);
            if (!m) continue;
            let evt: { type: string; kind?: 'created' | 'updated'; message?: MessageSummary };
            try {
              evt = JSON.parse(m[1]!);
            } catch {
              continue;
            }
            if (evt.type !== 'message' || !evt.message) continue;
            const incoming = evt.message;
            // Filter to the rendered slice. caseId mode shows only
            // that case's messages; clientId mode shows the whole
            // client; channel may also restrict.
            if (channel && incoming.channel !== channel) continue;
            if (caseId) {
              if (incoming.caseId !== caseId) continue;
            } else if (incoming.clientId !== clientId) continue;
            setMessages((prev) => {
              const idx = prev.findIndex((p) => p.id === incoming.id);
              if (idx === -1) {
                if (evt.kind === 'updated') return prev; // out-of-order; ignore
                return [...prev, incoming];
              }
              const next = prev.slice();
              next[idx] = incoming;
              return next;
            });
          }
        }
      } catch (err) {
        // AbortError on unmount is expected; surface anything else as a
        // soft warning — the user can still poll-via-reload.
        if ((err as Error).name !== 'AbortError') {
          // eslint-disable-next-line no-console
          console.warn('[wa-stream] disconnected', err);
        }
      }
    })();

    return () => {
      ctrl.abort();
      streamAbortRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, caseId, channel]);

  async function handleSend(e: FormEvent) {
    e.preventDefault();
    if (!body.trim()) return;
    setSending(true);
    setError(null);
    try {
      await apiFetch('/api/messages/wa', {
        method: 'POST',
        body: JSON.stringify({
          clientId,
          ...(caseId ? { caseId } : {}),
          body: body.trim(),
        }),
      });
      setBody('');
      void load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSending(false);
    }
  }

  const grouped = useMemo(() => groupByDay(messages, i18n.language), [messages, i18n.language]);

  if (loading && messages.length === 0) {
    return <div className="empty">{t('thread.loading')}</div>;
  }

  // Multi-channel mode (case Messages tab): render the list even without
  // a phone, since email/sms history can still be relevant. The compose
  // form is hidden when no phone is available.
  if (!clientPhone && channel === 'wa') {
    return <div className="empty">{t('thread.no_phone')}</div>;
  }

  const canSend = waStatus === 'connected' && !!clientPhone;

  return (
    <div className="msg-thread">
      <div className="msg-thread-list">
        {messages.length === 0 ? (
          <div className="empty">{t('thread.empty')}</div>
        ) : (
          grouped.map(({ day, items }) => (
            <div key={day}>
              <div className="msg-day-divider">{day}</div>
              {items.map((m) => {
                // Inbound = the customer. Show their name as a small
                // header on each inbound bubble so it's unambiguous
                // which side a message came from at a glance.
                const inboundLabel =
                  m.direction === 'in'
                    ? (clientName?.trim() || m.remote || t('thread.sender_unknown'))
                    : null;
                return (
                  <div
                    key={m.id}
                    className={`msg-bubble msg-${m.direction === 'out' ? 'out' : 'in'}`}
                  >
                    {inboundLabel && <div className="msg-sender">{inboundLabel}</div>}
                    {m.body && <div className="msg-body">{m.body}</div>}
                    <div className="msg-meta">
                      {!channel && (
                        <span className="msg-status">{t(`channel_label.${m.channel}`)} · </span>
                      )}
                      {new Date(m.createdAt).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}
                      {m.direction === 'out' && (
                        <span className={`msg-status ${m.status === 'failed' ? 'failed' : ''}`}>
                          · {t(STATUS_KEY[m.status])}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>

      {clientPhone && !canSend && (
        <div className="empty" style={{ padding: 8, fontSize: 11 }}>
          {t('thread.not_connected')}
        </div>
      )}

      {error && <div className="signin-error" style={{ marginBottom: 6 }}>{error}</div>}

      {clientPhone && (
        <form className="msg-compose" onSubmit={handleSend}>
          <input
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={t('thread.send_placeholder')}
            disabled={!canSend || sending}
            style={{ flex: 1, padding: '8px 12px', border: '1px solid var(--border2)', borderRadius: 20 }}
          />
          <button
            type="submit"
            className="btn-save"
            disabled={!canSend || sending || !body.trim()}
            style={{ padding: '8px 18px' }}
          >
            {sending ? t('thread.sending') : t('thread.send')}
          </button>
        </form>
      )}
    </div>
  );
}

function groupByDay(messages: MessageSummary[], locale: string) {
  const groups = new Map<string, MessageSummary[]>();
  for (const m of messages) {
    const day = new Date(m.createdAt).toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
    const cur = groups.get(day) ?? [];
    cur.push(m);
    groups.set(day, cur);
  }
  return Array.from(groups.entries()).map(([day, items]) => ({ day, items }));
}
